// src/pages/pipeline/PipelinePage.jsx
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Plus, GripVertical, Calendar, FileText,
  CheckCircle, XCircle, Info, Loader2, Settings, AlertCircle, Clock
} from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationContext';
import { pipelineAPI } from '@/services/pipeline';
import { projectsAPI } from '@/services/projects';
import { pipelineStatusesAPI } from '@/services/pipelineStatuses';
import { cn } from '@/lib/utils';


const PipelinePage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();

  const [projects, setProjects] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggingId, setDraggingId] = useState(null);


  // ✅ Dynamic Color Configuration
  const getColorConfig = (colorScheme) => {
    const colorMap = {
      'blue': {
        bg: 'bg-blue-50/60',
        headerBg: 'bg-blue-100',
        headerText: 'text-blue-900',
        borderColor: 'border-blue-200',
        accentColor: 'border-l-blue-500',
        countBg: 'bg-blue-600',
        iconColor: 'text-blue-600'
      },
      'orange': {
        bg: 'bg-orange-50/60',
        headerBg: 'bg-orange-100',
        headerText: 'text-orange-900',
        borderColor: 'border-orange-200',
        accentColor: 'border-l-orange-500',
        countBg: 'bg-orange-600',
        iconColor: 'text-orange-600'
      },
      'purple': {
        bg: 'bg-purple-50/60',
        headerBg: 'bg-purple-100',
        headerText: 'text-purple-900',
        borderColor: 'border-purple-200',
        accentColor: 'border-l-purple-500',
        countBg: 'bg-purple-600',
        iconColor: 'text-purple-600'
      },
      'green': {
        bg: 'bg-green-50/60',
        headerBg: 'bg-green-100',
        headerText: 'text-green-900',
        borderColor: 'border-green-200',
        accentColor: 'border-l-green-600',
        countBg: 'bg-green-600',
        iconColor: 'text-green-600'
      },
      'red': {
        bg: 'bg-red-50/60',
        headerBg: 'bg-red-100',
        headerText: 'text-red-900',
        borderColor: 'border-red-200',
        accentColor: 'border-l-red-500',
        countBg: 'bg-red-600',
        iconColor: 'text-red-600'
      },
      'slate': {
        bg: 'bg-slate-50/60',
        headerBg: 'bg-slate-100',
        headerText: 'text-slate-900',
        borderColor: 'border-slate-200',
        accentColor: 'border-l-slate-500',
        countBg: 'bg-slate-600',
        iconColor: 'text-slate-600'
      },
      'amber': {
        bg: 'bg-amber-50/60',
        headerBg: 'bg-amber-100',
        headerText: 'text-amber-900',
        borderColor: 'border-amber-200',
        accentColor: 'border-l-amber-500',
        countBg: 'bg-amber-600',
        iconColor: 'text-amber-600'
      }
    };

    return colorMap[colorScheme] || colorMap['blue'];
  };


  // ✅ Dynamic Icon Mapping
  const getIcon = (iconName) => {
    const iconMap = {
      'Info': Info,
      'Calendar': Calendar,
      'FileText': FileText,
      'CheckCircle': CheckCircle,
      'XCircle': XCircle,
      'Clock': Clock,
      'AlertCircle': AlertCircle,
    };
    return iconMap[iconName] || Info;
  };


  useEffect(() => {
    fetchData();
  }, []);


  const fetchData = async () => {
    try {
      setLoading(true);

      const statusResponse = await pipelineStatusesAPI.list();
      if (statusResponse.success) {
        setStatuses(statusResponse.data || []);
      }

      const projectResponse = await pipelineAPI.listProjects();
      if (projectResponse.success) {
        setProjects(projectResponse.data || []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      addNotification('Error', 'Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };


  const handleDragStart = (e, projectId) => {
    e.dataTransfer.setData('projectId', projectId);
    setDraggingId(projectId);
  };


  const handleDragEnd = () => {
    setDraggingId(null);
  };


  const handleDragOver = (e) => {
    e.preventDefault();
  };


  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    const projectId = e.dataTransfer.getData('projectId');
    setDraggingId(null);

    const project = projects.find(p => p.id === projectId);
    if (!project || project.status === newStatus) return;

    const oldStatus = project.status;

    // Optimistic update
    setProjects(prevProjects =>
      prevProjects.map(p =>
        p.id === projectId ? { ...p, status: newStatus } : p
      )
    );

    try {
      const response = await pipelineAPI.updateStatus(projectId, newStatus);

      if (response.success) {
        addNotification(
          'Success',
          `Moved "${project.project_name}" to ${newStatus}`,
          'success'
        );
      }
    } catch (error) {
      // Revert on error
      setProjects(prevProjects =>
        prevProjects.map(p =>
          p.id === projectId ? { ...p, status: oldStatus } : p
        )
      );
      console.error('Failed to update status:', error);
      addNotification('Error', error.message || 'Failed to update status', 'error');
    }
  };


  // ✅ Follow Up Counter Handler
 const handleFollowupChange = async (e, project, action) => {
  e.stopPropagation();

  // منع الضغط المتكرر السريع
  if (!project.id) return;

  try {
    const result = await projectsAPI.updateFollowup(project.id, action);

    if (result?.success) {
      // ✅ اعتمد على الـ API response مش الـ optimistic update
      setProjects(prev => prev.map(p =>
        p.id === project.id
          ? {
              ...p,
              followup_count: result.followup_count,
              last_followup_at: result.last_followup_at
            }
          : p
      ));
    }
  } catch (error) {
    console.error('Failed to update follow-up:', error);
    addNotification('Error', 'Failed to update follow-up', 'error');
  }
};



  const getProjectCount = (statusTitle) =>
    projects.filter(p => p.status === statusTitle).length;


  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }


  return (
    <>
      <Helmet>
        <title>Pipeline - Project Management</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Project Pipeline</h1>
            <p className="text-gray-600 mt-1">Manage project stages with drag and drop</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/pipeline/statuses')}
            >
              <Settings className="w-4 h-4 mr-2" />
              Manage Statuses
            </Button>
            <Button onClick={() => navigate('/projects/add')}>
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </div>
        </div>

        {/* Pipeline Board */}
        <div className="flex gap-4 overflow-x-auto pb-4">
          {statuses.map((status) => {
            const config = getColorConfig(status.color_scheme);
            const Icon = getIcon(status.icon_name);
            const columnProjects = projects.filter(p => p.status === status.title);
            const isFollowUp = status.title.trim() === 'Follow UP';

            return (
              <div
                key={status.id}
                className="flex flex-col h-[calc(100vh-200px)] min-w-[300px] flex-shrink-0"
              >
                {/* Column Header */}
                <div className={cn(
                  'p-4 rounded-t-lg border-b-2',
                  config.headerBg,
                  config.borderColor
                )}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Icon className={cn('w-5 h-5', config.iconColor)} />
                      <h3 className={cn('font-semibold text-sm', config.headerText)}>
                        {status.title}
                      </h3>
                    </div>
                    <Badge className={cn(config.countBg, 'text-white text-xs')}>
                      {getProjectCount(status.title)}
                    </Badge>
                  </div>
                </div>

                {/* Drop Zone */}
                <div
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, status.title)}
                  className={cn(
                    'flex-1 p-2 space-y-2 overflow-y-auto rounded-b-lg border-2 border-t-0',
                    config.bg,
                    config.borderColor
                  )}
                >
                  {columnProjects.length === 0 ? (
                    <p className="text-center text-gray-400 text-sm mt-8">
                      No projects
                    </p>
                  ) : (
                    columnProjects.map((project) => (
                      <Card
                        key={project.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, project.id)}
                        onDragEnd={handleDragEnd}
                        onClick={() => navigate(`/projects/${project.id}`)}
                        className={cn(
                          'cursor-move hover:shadow-md transition-all border-l-4',
                          config.accentColor,
                          draggingId === project.id && 'opacity-50'
                        )}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-medium text-sm line-clamp-2">
                              {project.project_name}
                            </h4>
                            <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          </div>
                          <p className="text-xs text-gray-600 mb-1">
                            {project.company_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            #{project.serial_number}
                          </p>

                          {/* ✅ Follow Up Counter — فقط في كولم Follow UP */}
                          {isFollowUp && (
                            <div
                              className="mt-2 pt-2 border-t border-amber-200"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-amber-700 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  متابعات
                                </span>
                                <div className="flex items-center gap-1">
                                  {/* زر تقليل */}
                                  <button
                                    onClick={(e) => handleFollowupChange(e, project, 'decrement')}
                                    className="w-5 h-5 rounded bg-amber-100 hover:bg-amber-200 text-amber-700 flex items-center justify-center text-xs font-bold transition-colors"
                                    title="تقليل"
                                  >
                                    ▼
                                  </button>
                                  {/* العداد */}
                                  <span className="min-w-[24px] text-center text-sm font-bold text-amber-800 bg-amber-100 rounded px-1">
                                    {project.followup_count || 0}
                                  </span>
                                  {/* زر زيادة */}
                                  <button
                                    onClick={(e) => handleFollowupChange(e, project, 'increment')}
                                    className="w-5 h-5 rounded bg-amber-100 hover:bg-amber-200 text-amber-700 flex items-center justify-center text-xs font-bold transition-colors"
                                    title="تسجيل متابعة"
                                  >
                                    ▲
                                  </button>
                                </div>
                              </div>

                              {/* آخر تاريخ متابعة */}
                              {project.last_followup_at && (
                                <p className="text-xs text-amber-600 mt-1">
                                  آخر تواصل:{' '}
                                  {new Date(project.last_followup_at).toLocaleDateString('ar-EG', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </p>
                              )}
                            </div>
                          )}

                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </>
  );
};

export default PipelinePage;
